import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, LoaderCircle, X } from "lucide-react";

type Props = {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  input?: { label: string; initialValue: string };
  onConfirm: (value: string) => Promise<void>;
  onClose: () => void;
};

/** Native modal supplies inert background and keyboard focus containment. */
export function AppActionDialog({ title, description, confirmLabel, danger, input, onConfirm, onClose }: Props) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState(input?.initialValue ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    if (!element) return;
    if (typeof element.showModal === "function") element.showModal();
    else element.setAttribute("open", ""); // DOM-only test environments.
    const first = element.querySelector<HTMLElement>("input, [data-cancel]");
    first?.focus();
    if (first instanceof HTMLInputElement) first.select();
    return () => {
      if (element.open && typeof element.close === "function") element.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    const name = value.trim();
    if (input && (!name || name === "." || name === ".." || /[\\/]/.test(name) || [...name].some((character) => character.charCodeAt(0) < 32))) {
      setError("Informe um nome válido, sem barras ou caracteres de controle.");
      return;
    }
    submitting.current = true;
    setPending(true);
    setError(null);
    try { await onConfirm(name); onClose(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Não foi possível concluir. Tente novamente."); }
    finally { submitting.current = false; setPending(false); }
  }

  return createPortal(
    <dialog ref={dialog} className="app-action-dialog" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} aria-modal="true" onCancel={(event) => { event.preventDefault(); if (!submitting.current) onClose(); }} onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); if (!submitting.current) onClose(); } }}>
      <form onSubmit={(event) => void submit(event)}>
        <header><h2 id={`${id}-title`}>{title}</h2><button type="button" className="icon-action" aria-label="Fechar diálogo" disabled={pending} onClick={onClose}><X size={18} /></button></header>
        <p id={`${id}-description`}>{description}</p>
        {input && <label htmlFor={`${id}-input`}>{input.label}<input id={`${id}-input`} value={value} maxLength={255} disabled={pending} onChange={(event) => setValue(event.target.value)} autoComplete="off" required /></label>}
        {error && <div className="form-error" role="alert"><AlertCircle size={17} /><span>{error}</span></div>}
        <footer><button data-cancel type="button" className="secondary-button" disabled={pending} onClick={onClose}>Cancelar</button><button type="submit" className={`primary-button compact${danger ? " destructive" : ""}`} disabled={pending}>{pending ? <><LoaderCircle className="spin" size={16} />Processando…</> : confirmLabel}</button></footer>
      </form>
    </dialog>, document.body,
  );
}
